import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Modal - Renders children at the root level of the DOM
 * This ensures modals display correctly above all content including
 * animated containers that use CSS transforms.
 */
export default function Modal({ children, onClose, className = '' }) {
  const [mounted, setMounted] = useState(false)
  
  useEffect(() => {
    setMounted(true)
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden'
    return () => {
      setMounted(false)
      document.body.style.overflow = ''
    }
  }, [])
  
  if (!mounted) return null
  
  return createPortal(
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 ${className}`}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) {
          onClose()
        }
      }}
    >
      {children}
    </div>,
    document.body
  )
}

/**
 * SlideUpModal - Slides up from bottom (for menus)
 */
export function SlideUpModal({ children, onClose }) {
  const [mounted, setMounted] = useState(false)
  
  useEffect(() => {
    setMounted(true)
    document.body.style.overflow = 'hidden'
    return () => {
      setMounted(false)
      document.body.style.overflow = ''
    }
  }, [])
  
  if (!mounted) return null
  
  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex"
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) {
          onClose()
        }
      }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      {/* Content slides from left/bottom */}
      {children}
    </div>,
    document.body
  )
}

/**
 * OverlayPortal — takes an EXISTING hand-rolled overlay and puts it on the
 * viewport, without touching a byte of its markup.
 *
 * ★★★ WHY THIS AND NOT `Modal` ABOVE. `Modal` supplies its own backdrop, z-index
 *   and padding, so adopting it changes how ten overlays LOOK across five
 *   screens that cannot be verified signed-out. This changes only WHERE they are
 *   anchored, which is the entire defect. The owner is still one component; it
 *   just owns less.
 *
 * ★★★ THE DEFECT, MEASURED 2026-09-07 AT 402x874 AGAINST THE REAL BUILT CSS.
 *   Every routed screen renders inside PageWrapper, a framer-motion motion.div.
 *   A transformed ancestor is the containing block for `position: fixed`, so
 *   `fixed inset-0` resolves to THE PAGE, not the viewport:
 *
 *     page height   centered modal        bottom sheet
 *       874 px      on screen             on screen
 *      1200 px      on screen             258 px below the fold
 *      1748 px      on screen             806 px below the fold
 *      2400 px      302 px below          1458 px below
 *      4000 px      1102 px below         3058 px below
 *
 *   ★★ A BOTTOM SHEET IS OFF SCREEN THE MOMENT THE PAGE SCROLLS AT ALL. A
 *      centered modal survives until the page passes about two viewports and
 *      then goes the same way — which is why this shipped: it works perfectly on
 *      a short page and on every screenshot anybody took.
 *   ★ AND THE TRANSFORM IS THE IDENTITY. Measured `matrix(1, 0, 0, 1, 0, 0)` —
 *     framer-motion leaves `translateY(0px)` on the element at rest, and an
 *     identity transform establishes a containing block exactly as a real one
 *     does. Nothing has to be animating for this to be broken.
 */
export function OverlayPortal({ children }) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true); return () => setMounted(false) }, [])
  if (!mounted) return null
  return createPortal(children, document.body)
}
